import React, { lazy, Suspense } from 'react'
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  redirect,
} from 'react-router-dom'
import { UserProvider } from './contexts/userContext'
import { SubscriptionCountProvider } from './contexts/subscriptionCounterContext'
import { MenuItemsCounterProvider } from './contexts/menuItemsCounterContext'
import Layout from './components/layout/layout'
import PrivateRoute from './features/profile/components/PrivateRoute'
import LoadingFallback from './components/LoadingFallback'
import { checkPermission } from './components/layout/Authorization'
import permissions from './features/configuration/utils/rolesPermissions'
import RouterErrorBoundary from './components/RouterErrorBoundary'
import { Toaster } from 'sonner'
import { ThemeProvider } from './contexts/themeContext'
import AuthLayout from './components/layout/AuthLayout/AuthLayout'
import PublicRoute from './guards/PublicRoute'
import { usePermissionsStore } from './services/permissionsStore'

const LazyDashboard = lazy(() => import('./features/dashboard/pages/Dashboard'))
const LazyBranches = lazy(() => import('./features/dashboard/pages/Branches'))
const LazyBranch = lazy(() => import('./features/dashboard/pages/Branch'))
const LazyDepartment = lazy(() => import('./features/dashboard/pages/Department'))
const LazyOrgChart = lazy(() => import('./features/organizationChart/pages/OrgChart'))
const LazyWorkforce = lazy(() => import('./features/organizationChart/pages/WorkforceContainer'))
const LazyProfile = lazy(() => import('./features/profile/pages/Profile'))
const LazyConfiguration = lazy(
  () => import('./features/configuration/pages/Configuration'),
)
const LazyDefaults = lazy(
  () => import('./features/defaults/pages/Defaults'),
)
const LazySystemDefaults = lazy(
  () => import('./features/systemDefaults/pages/SystemDefaults'),
)
const LazyReports = lazy(() => import('./features/reports/pages/Reports'))
const LazyChartsReports = lazy(
  () => import('./features/reports/pages/ChartsReports'),
)
const LazyTimeToFillReport = lazy(
  () => import('./features/reports/pages/TimeToFillReport'),
)
const LazyTimeToHireReport = lazy(
  () => import('./features/reports/pages/TimeToHireReport'),
)
const LazyHiringManagerReport = lazy(
  () => import('./features/reports/pages/HiringManagerReport'),
)
const LazyPersonalCalendar = lazy(
  () => import('./features/calendar/pages/PersonalCalendar'),
)
const LazyInterviewCalendar = lazy(
  () => import('./features/calendar/pages/InterviewCalendar'),
)
const LazyCandidates = lazy(
  () => import('./features/candidates/pages/candidates'),
)
const LazyBlacklistCandidates = lazy(
  () => import('./features/candidates/pages/blacklistCandidates'),
)
const LazyBlockedCandidates = lazy(
  () => import('./features/candidates/pages/blockedCandidates'),
)
const LazyTalentPools = lazy(
  () => import('./features/talentPools/pages/TalentPools'),
)
const LazyTalentPoolDetails = lazy(
  () => import('./features/talentPools/pages/TalentPoolDetails'),
)
const LazyPositions = lazy(() => import('./features/positions/pages/positions'))
const LazyPositionForm = lazy(
  () => import('./features/positions/pages/PositionForm'),
)
const LazyPosition = lazy(() => import('./features/positions/pages/position'))
const LazyStageInterviews = lazy(
  () => import('./features/positions/pages/stageInterviews'),
)
const LazyInternalApplications = lazy(
  () => import('./features/positions/pages/internalApplications'),
)
const LazyInterviews = lazy(
  () => import('./features/interviews/pages/interviews'),
)
const LazyPrepareInterview = lazy(
  () => import('./features/interviews/pages/prepareInterview'),
)
const LazyRequisitionManagement = lazy(
  () => import('./features/requisitions/pages/RequisitionManagement'),
)
const LazyRequisitionDetails = lazy(
  () =>
    import(
      './features/requisitions/components/requisitions/RequisitionDetails'
    ),
)
// const LazyRequisitionForm = lazy(() => import('./Dashboard/pages/RequisitionForm'));
const LazyRequisitionCreationContainer = lazy(
  () => import('./features/requisitions/pages/RequisitionCreationContainer'),
)
const LazyJobRequests = lazy(
  () => import('./features/approvalRequests/pages/jobRequests'),
)
const LazyOrganizations = lazy(
  () => import('./features/organizations/pages/organizations'),
)
const LazyOrganization = lazy(
  () => import('./features/organizations/pages/Organization'),
)
const LazyOrganizationForm = lazy(
  () => import('./features/organizations/pages/OrganizationForm'),
)
const LazyOrganizationInfo = lazy(
  () => import('./features/organizations/pages/SingleOrganizationDetails'),
)
const LazyEvaluationForms = lazy(
  () => import('./features/evaluationForms/pages/EvaluationForms'),
)
const LazyDataManagement = lazy(
  () => import('./features/dataManagement/pages/dataManagement'),
)
const LazyNotifications = lazy(
  () => import('./features/notifications/pages/Notifications'),
)
const LazyNotFound = lazy(() => import('./features/profile/pages/NotFound'))
const LazyApprovalRequest = lazy(
  () => import('./features/approvalRequests/pages/approvalRequest'),
)
const LazyUploadRequestedDocuments = lazy(
  () => import('./features/interviews/pages/UploadRequestedDocuments'),
)
const LazySubscriptionSuccessPage = lazy(
  () => import('./features/subscriptions/pages/SubscriptionSuccessPage'),
)
const LazyPlanSelectionPage = lazy(
  () =>
    import(
      './features/subscriptions/components/userConfiguration/PlanSelection'
    ),
)
const LazyGreenhouseProfilePage = lazy(
  () => import('./features/greenhouseProfile/pages/InterviewAnalysis'),
)
const LazyAssistinoPage = lazy(
  () => import('../src/features/Assistino/pages/Assistino'),
)

const LazyDisplaySettings = lazy(
  () => import('./features/settings/pages/DisplaySettings'),
)

const LazyReferenceRequestForm = lazy(
  () => import('./features/referenceCheck/pages/ReferenceRequestForm'),
)

const LazyReferenceFeedbackForm = lazy(
  () => import('./features/referenceCheck/pages/ReferenceFeedbackForm'),
)
const LazyConversations = lazy(
  () => import('./features/Conversations/pages/Conversations'),
)

const LazyPublicVideoLink = lazy(
  () => import('./features/interviews/pages/PublicVideoLink'),
)

// ===== Auth
const Login = lazy(() => import('./features/auth/pages/Login'))
const Registration = lazy(() => import('./features/auth/pages/Registration'))
const ForgetPassword = lazy(
  () => import('./features/auth/pages/ForgetPassword'),
)
const ResetPassword = lazy(() => import('./features/auth/pages/ResetPassword'))
const UserRegistration = lazy(
  () => import('./features/auth/pages/UserRegistration'),
)
const ExpiredLink = lazy(() => import('./features/auth/pages/ExpiredLink'))
const MainLayout = () => (
  <MenuItemsCounterProvider>
    <Layout>
      <Suspense fallback={<LoadingFallback />}>
        <Outlet />
      </Suspense>
    </Layout>
  </MenuItemsCounterProvider>
)

const createPermissionLoader = (permission) => async () => {
  const store = usePermissionsStore.getState()
  if (!store.loaded && !store.loading) {
    await store.ensureLoaded()
  }
  const hasPermission = checkPermission(permission)
  if (!hasPermission) throw redirect('/not-found')
  return null
}

const routerConfig = [
  {
    path: '/',
    element: <PrivateRoute />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        element: <MainLayout />,
        children: [
          {
            element: (
              <SubscriptionCountProvider>
                <Outlet />
              </SubscriptionCountProvider>
            ),
            children: [
              {
                path: '/',
                element: <LazyDashboard />,
                // loader: createPermissionLoader(permissions.companyDashboard)
              },
              {
                path: '/branches',
                element: <LazyBranches />,
              },
              {
                path: '/branches/:id',
                element: <LazyBranch />,
              },
              {
                path: '/departments/:id',
                element: <LazyDepartment />,
              },
              {
                path: '/profile',
                element: <LazyProfile />,
                // loader: createPermissionLoader(permissions.companyUpdate),
              },
              {
                path: '/organization-chart',
                element: <LazyOrgChart />,
                // loader: createPermissionLoader(permissions.companyUpdate),
              },
              {
                path: '/assistino',
                element: <LazyAssistinoPage />,
              },
              {
                path: '/assistino/:id',
                element: <LazyAssistinoPage />,
              },
              {
                path: '/workforce-structure',
                element: <LazyWorkforce />,
              },
              {
                path: '/configuration',
                element: <LazyConfiguration />,
                loader: createPermissionLoader(permissions.roleReadAll),
              },
              {
                path: '/defaults',
                element: <LazyDefaults />,
                loader: createPermissionLoader(permissions.userReadAll),
              },
              {
                path: '/system-defaults',
                element: <LazySystemDefaults />,
                loader: createPermissionLoader(permissions.userReadAll),
              },
              {
                path: '/local-candidates',
                element: <LazyCandidates />,
                loader: createPermissionLoader(permissions.candidateReadAll),
              },
              {
                path: '/blacklist-candidates',
                element: <LazyBlacklistCandidates />,
                loader: createPermissionLoader(
                  permissions.candidateReadBlacklisted,
                ),
              },
              {
                path: '/blocked-candidates',
                element: <LazyBlockedCandidates />,
                loader: createPermissionLoader(
                  permissions.candidateReadBlacklisted,
                ),
              },
              {
                path: '/talent-pools',
                element: <LazyTalentPools />,
                loader: createPermissionLoader(permissions.candidateRead),
              },
              {
                path: '/talent-pools/:id',
                element: <LazyTalentPoolDetails />,
                loader: createPermissionLoader(permissions.candidateRead),
              },
              {
                path: '/positions',
                element: <LazyPositions />,
                loader: createPermissionLoader(permissions.positionRead),
              },
              { path: '/positions/new', element: <LazyPositionForm /> },
              { path: '/positions/:id', element: <LazyPosition /> },
              {
                path: '/positions/:positionId/stages/:stageId',
                element: <LazyStageInterviews />,
              },
              {
                path: '/positions/:positionId/internal-applications',
                element: <LazyInternalApplications />,
              },
              {
                path: '/requisitions',
                element: <LazyRequisitionManagement />,
                loader: createPermissionLoader(
                  permissions.requisitionsReadHistory,
                ),
              },
              {
                path: '/requisitions/new',
                element: <LazyRequisitionCreationContainer />,
                loader: createPermissionLoader(permissions.requisitionsCreate),
              },
              {
                path: '/requisitions/:id',
                element: <LazyRequisitionDetails />,
              },
              {
                path: '/job-requests',
                element: <LazyJobRequests />,
                loader: createPermissionLoader(permissions.jobRequestsRead),
              },
              {
                path: '/organizations',
                element: <LazyOrganizations />,
                loader: createPermissionLoader(permissions.organizationReadAll),
              },
              { path: '/organizations/new', element: <LazyOrganizationForm /> },
              { path: '/organizations/:id', element: <LazyOrganization /> },
              {
                path: '/organizations/:id/info',
                element: <LazyOrganizationInfo />,
              },
              { path: '/interviews/:id', element: <LazyInterviews /> },
              {
                path: '/interviews/:token/questions',
                element: <LazyPrepareInterview />,
              },
              { path: '/success', element: <LazySubscriptionSuccessPage /> },
              { path: '/plan-selection', element: <LazyPlanSelectionPage /> },
              {
                path: '/data-management',
                element: <LazyDataManagement />,
                loader: createPermissionLoader(permissions.reportsSourcing),
              },
              {
                path: '/conversations',
                element: <LazyConversations />,
                // loader: createPermissionLoader(permissions.reportsSourcing),
              },
            ],
          },
          {
            children: [
              {
                path: '/reports/sourcing',
                element: <LazyReports />,
                loader: createPermissionLoader(permissions.reportsSourcing),
              },
              {
                path: '/reports/charts/:category',
                element: <LazyChartsReports />,
              },
              {
                path: '/reports/time-to-fill',
                element: <LazyTimeToFillReport />,
                loader: createPermissionLoader(permissions.reportsSourcing),
              },
              {
                path: '/reports/time-to-hire',
                element: <LazyTimeToHireReport />,
                loader: createPermissionLoader(permissions.reportsSourcing),
              },
              {
                path: '/reports/hiring-manager-performance',
                element: <LazyHiringManagerReport />,
                loader: createPermissionLoader(permissions.reportsSourcing),
              },

              {
                path: '/personal-calendar',
                element: <LazyPersonalCalendar />,
                loader: createPermissionLoader(
                  permissions.userPersonalCalendar,
                ),
              },
              {
                path: '/interview-schedule',
                element: <LazyInterviewCalendar />,
                loader: createPermissionLoader(
                  permissions.reportsInterviewSchedule,
                ),
              },
              { path: '/notifications', element: <LazyNotifications /> },
              {
                path: '/evaluation-forms',
                element: <LazyEvaluationForms />,
                loader: createPermissionLoader(
                  permissions.evaluationFormsReadAll,
                ),
              },
              {
                path: '/display-settings',
                element: <LazyDisplaySettings />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    element: <AuthLayout />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        path: '/login',
        element: <Login />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/register',
        element: <Registration />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/forget-password',
        element: <ForgetPassword />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/reset-password/:token',
        element: <ResetPassword />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/user-registration/:token',
        element: <UserRegistration />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/expired-link',
        element: <ExpiredLink />,
        errorElement: <RouterErrorBoundary />,
      },
    ],
  },
  {
    path: '/greenhouse/profile/:token',
    element: <LazyGreenhouseProfilePage />,
  },
  {
    path: '/approval-request/:approvalToken',
    element: <LazyApprovalRequest />,
    errorElement: <RouterErrorBoundary />,
  },
  {
    path: '/request-documents/:token',
    element: <LazyUploadRequestedDocuments />,
    errorElement: <RouterErrorBoundary />,
  },
  {
    path: '/reference-request-form/:requestToken',
    element: <LazyReferenceRequestForm />,
  },
  {
    path: '/reference-feedback-form/:contactToken',
    element: <LazyReferenceFeedbackForm />,
  },
  {
    path: '/interview/:interview_id/question/:question_id/video',
    element: <LazyPublicVideoLink />,
  },
  {
    path: '*',
    element: <LazyNotFound />,
    errorElement: <RouterErrorBoundary />,
  },
]

const router = createBrowserRouter(routerConfig, {
  basename: '/',
})

function App() {
  return (
    <UserProvider>
      <ThemeProvider>
        <RouterProvider router={router} />
        <Toaster richColors />
      </ThemeProvider>
    </UserProvider>
  )
}

export default App
